/**
 * In-memory token store (security-baseline §1.3).
 *
 * Tokens NEVER touch localStorage / sessionStorage. They live in a module
 * closure for the lifetime of the tab. On reload the access token is gone —
 * AuthProvider's silent refresh against the HttpOnly refresh-cookie restores
 * a session if one exists.
 *
 * The refresh token never enters this store either: it lives only in the
 * server-set HttpOnly cookie (see backend/src/modules/auth/cookieHelpers.mjs).
 *
 * The function names (readStoredTokens / writeStoredTokens / clearStoredTokens)
 * are preserved so existing call sites (AuthProvider, api/client) keep working.
 */

export type SessionTokens = {
  accessToken?: string
  idToken?: string
  /**
   * @deprecated Refresh token now lives in HttpOnly cookie set by the backend.
   * Field kept only so legacy login response shapes can be assigned without a
   * type error; the value is dropped on write.
   */
  refreshToken?: string
  tokenType?: string
  expiresAt?: number
}

const LEGACY_STORAGE_KEYS = [
  'tc:access_token',
  'tc:id_token',
  'tc:refresh_token',
  'tc:token_type',
  'tc:expires_at',
] as const

/**
 * Presence-only hint that a session likely exists ('1' or absent). NOT
 * a token and carries no PII — §1.3 forbids tokens in storage, not a
 * boolean. The HttpOnly refresh cookie is invisible to JS, so without
 * this the app cannot tell "anonymous visitor" from "returning user"
 * until /auth/refresh answers; route-entry prefetches use it to start
 * anonymous public fetches immediately (perf F7 follow-up). Stale
 * values only change WHEN a prefetch starts, never what is rendered.
 */
const SESSION_HINT_KEY = 'tc:session-hint'

let memoryTokens: SessionTokens | null = null

function isBrowser(): boolean {
  return typeof window !== 'undefined'
}

/**
 * One-shot eviction of any tokens left over from the previous localStorage
 * scheme. Safe to call repeatedly.
 */
function purgeLegacyStorage(): void {
  if (!isBrowser()) return
  for (const key of LEGACY_STORAGE_KEYS) {
    try {
      window.localStorage.removeItem(key)
    } catch {
      // localStorage may be blocked (private mode); ignore.
    }
  }
}

purgeLegacyStorage()

export function readStoredTokens(): SessionTokens | null {
  return memoryTokens
}

export function writeStoredTokens(tokens: SessionTokens): void {
  // refreshToken stripped on write — it belongs in the HttpOnly cookie only.
  const { refreshToken: _ignored, ...withoutRefresh } = tokens
  void _ignored
  memoryTokens = withoutRefresh
  try {
    window.localStorage.setItem(SESSION_HINT_KEY, '1')
  } catch {
    // localStorage may be blocked (private mode); the hint is optional.
  }
}

export function clearStoredTokens(): void {
  memoryTokens = null
  try {
    window.localStorage.removeItem(SESSION_HINT_KEY)
  } catch {
    // localStorage may be blocked (private mode); the hint is optional.
  }
}

/** Synchronous best-effort "a session probably exists here" check —
 * see SESSION_HINT_KEY. False negatives/positives only delay or add
 * one public prefetch; they never affect what is rendered. */
export function hasSessionHint(): boolean {
  try {
    return window.localStorage.getItem(SESSION_HINT_KEY) === '1'
  } catch {
    return false
  }
}

export function hasActiveAccessToken(tokens: SessionTokens | null): boolean {
  if (!tokens?.accessToken) return false
  if (!tokens.expiresAt) return true
  return tokens.expiresAt > Date.now()
}
